import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useThree } from "@react-three/fiber";
import {
  Box3Helper,
  BufferAttribute,
  BufferGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  OrthographicCamera,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  Vector3,
} from "three";
import { Alert, ButtonBase, Dialog, Stack, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { CreateAnnotationForm } from "@/components/ui/annotations/create-annotation-form";
import { useAnnotationScreenshot } from "@/components/ui/annotations/use-annotation-screenshot";
import { resizeImage, type FileDetails } from "@faro-lotv/flat-ui";
import { generateGUID } from "@faro-lotv/foundation";
import type { AnnotationCreationData } from "@/components/ui/annotations/annotation-props";
import { isExternalAnnotationData } from "@/components/ui/annotations/annotation-props";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  reviewClash,
  selectClash,
  setClashReviewOrigin,
  type CadPointCloudAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  cameraToAnnotationCameraParams,
  useCreateSinglePointAnnotation,
} from "@/tools/annotation-picker/use-create-single-point-annotation";
import { useCadClashAnalysis } from "@/tools/cad-analysis/use-cad-clash-analysis";
import { clashCameraView } from "@/tools/cad-analysis/cad-clash-camera";
import type { ClashCandidate } from "@/tools/cad-analysis/cad-clash-detection";
import {
  clashCandidateBounds,
  clashDistanceLabel,
} from "@/tools/cad-analysis/cad-clash-detection";
import type { ICadModel } from "@faro-lotv/lotv";
import { IElementTypeHint } from "@faro-lotv/ielement-types";
import type { PointCloudObject } from "@/object-cache";
import { TextLabel } from "../text-label";
import { selectClashReviewGroups } from "@/tools/cad-analysis/clash-review-groups";

const MIN_ASPECT = 0.2;
const PANEL_WIDTH = 480;
const OUTLINE_COLOR = 0xff7373;

/** @returns Clash markers, inspection camera commands and the existing annotation dialog. */
export function CadClashReview({
  cad,
  cloud,
  analysis,
  budget,
}: {
  cad: ICadModel;
  cloud: PointCloudObject;
  analysis: CadPointCloudAnalysis;
  budget: number;
}): JSX.Element {
  useCadClashAnalysis(cad, cloud, analysis, budget);
  useCadClashAnnotation(analysis);
  const dispatch = useAppDispatch();
  const selected = useAppSelector(
    (state) => state.pointCloudAnalysisTool.selectedClash,
  );
  const status = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashStatus?.[analysis.id],
  );
  const mode = useAppSelector(selectModeName);
  const events = useSceneEvents();
  const { camera, size } = useThree();
  const ready = status?.analysisId === analysis.id && status.phase === "ready";
  const origin = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashReviewOrigins?.[analysis.id],
  );
  const tab = useAppSelector((state) => state.pointCloudAnalysisTool.panelTab);
  const groups = selectClashReviewGroups(analysis, origin);
  const candidate = groups.find(
    (group) =>
      selected?.analysisId === analysis.id &&
      group.memberIds.includes(selected.candidateId),
  )?.candidate;
  useEffect(() => {
    if (ready && tab === "clashes") {
      dispatch(
        setClashReviewOrigin({
          analysisId: analysis.id,
          origin: camera.getWorldPosition(new Vector3()).toArray(),
        }),
      );
    }
  }, [ready, tab, analysis.id, camera, dispatch]);
  useEffect(() => {
    if (!ready || !candidate || candidate.status === "dismissed") return;
    if (mode !== "overview") {
      dispatch(changeMode("overview"));
      return;
    }
    if (
      !(
        camera instanceof PerspectiveCamera ||
        camera instanceof OrthographicCamera
      )
    ) {
      return;
    }
    events.setCameraView.emit(
      clashCameraView(
        camera,
        candidate,
        Math.max(MIN_ASPECT, (size.width - PANEL_WIDTH) / size.height),
        analysis.clashes?.boundsFrame,
      ),
    );
  }, [
    selected,
    ready,
    candidate,
    analysis.clashes?.boundsFrame,
    mode,
    camera,
    events,
    dispatch,
    size.width,
    size.height,
  ]);
  return (
    <CadClashMarkers
      analysis={analysis}
      candidate={candidate}
      visible={ready}
    />
  );
}

/** @param analysis Comparison owning the selected finding and annotation draft. */
export function useCadClashAnnotation(analysis: CadPointCloudAnalysis): void {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const selected = useAppSelector(
    (state) => state.pointCloudAnalysisTool.selectedClash,
  );
  const status = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashStatus?.[analysis.id],
  );
  const writable = useAppSelector(selectHasWritePermission);
  const events = useSceneEvents();
  const camera = useThree((state) => state.camera);
  const { setContent } = useUiOverlayContext();
  const createAnnotation = useCreateSinglePointAnnotation();
  const takeScreenshot = useAnnotationScreenshot();
  const capturing = useRef(new Set<string>());
  const [draft, setDraft] = useState<{
    candidate: ClashCandidate;
    fingerprint: string;
    memberIds: string[];
    initialAttachment?: FileDetails;
    screenshotWarning?: string;
    cameraParams: ReturnType<typeof cameraToAnnotationCameraParams>;
  }>();
  const [error, setError] = useState<string>();
  const saving = useRef(new Set<string>());
  const ready = status?.analysisId === analysis.id && status.phase === "ready";
  const origin = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashReviewOrigins?.[analysis.id],
  );
  const tab = useAppSelector((state) => state.pointCloudAnalysisTool.panelTab);
  const groups = selectClashReviewGroups(analysis, origin);
  useEffect(() => {
    let active = true;
    const subscription = events.cadClashCommand.on(async (request) => {
      if (
        request.analysisId !== analysis.id ||
        request.command !== "annotate" ||
        !writable ||
        !ready ||
        draft ||
        capturing.current.size > 0 ||
        saving.current.size > 0
      ) {
        return;
      }
      const group = groups.find((entry) =>
        entry.memberIds.includes(request.candidateId ?? ""),
      );
      const item = group?.candidate;
      if (
        !item ||
        item.annotationId ||
        item.annotationFailure ||
        !analysis.clashes
      ) {
        return;
      }
      setError(undefined);
      capturing.current.add(item.id);
      const cameraParams = cameraToAnnotationCameraParams(camera);
      let initialAttachment: FileDetails | undefined;
      let screenshotWarning: string | undefined;
      try {
        const screenshot = await takeScreenshot();
        const thumbnailUrl = await resizeImage(screenshot).catch(() => null);
        initialAttachment = {
          id: generateGUID(),
          date: new Date().toISOString(),
          fileName: screenshot.name,
          fileSize: screenshot.size,
          urlOrFile: screenshot,
          thumbnailUrl,
        };
      } catch {
        screenshotWarning =
          "The screenshot could not be captured. You can attach an image manually.";
      } finally {
        capturing.current.delete(item.id);
      }
      if (active) {
        setDraft({
          candidate: item,
          fingerprint: analysis.clashes.fingerprint,
          memberIds: group.memberIds,
          initialAttachment,
          screenshotWarning,
          cameraParams,
        });
      }
    });
    return () => {
      active = false;
      subscription.dispose();
    };
  }, [
    events,
    analysis,
    writable,
    ready,
    draft,
    groups,
    camera,
    takeScreenshot,
    selected?.candidateId,
    selected?.analysisId,
    tab,
  ]);
  useEffect(() => {
    if (!draft) return;
    async function save(details: AnnotationCreationData): Promise<void> {
      if (!draft || saving.current.size > 0) return;
      const current = selectAnalysis(analysis.id)(store.getState());
      if (
        !writable ||
        current?.kind !== "cad" ||
        current.clashes?.fingerprint !== draft.fingerprint ||
        current.clashes.candidates.find(
          (item) => item.id === draft.candidate.id,
        )?.annotationFailure
      ) {
        setError(
          "The comparison changed. Close this form and review the current result.",
        );
        return;
      }
      saving.current.add(draft.candidate.id);
      setError(undefined);
      try {
        const annotationId = await createAnnotation(
          {
            points: [draft.candidate.position],
            parentId: analysis.parentId,
            type: IElementTypeHint.spaceAnnotation,
          },
          details,
          draft.cameraParams,
          { confirmPersistence: true },
        );
        dispatch(
          reviewClash({
            analysisId: analysis.id,
            candidateId: draft.candidate.id,
            candidateIds: draft.memberIds,
            fingerprint: draft.fingerprint,
            status: "annotated",
            annotationId,
          }),
        );
        dispatch(
          setObjectVisibility({
            type: ViewObjectTypes.annotations,
            visibility: true,
          }),
        );
        setDraft(undefined);
      } catch (failure) {
        if (isExternalAnnotationData(details)) {
          dispatch(
            reviewClash({
              analysisId: analysis.id,
              candidateId: draft.candidate.id,
              candidateIds: draft.memberIds,
              fingerprint: draft.fingerprint,
              status: "open",
              annotationFailure:
                "External save could not be confirmed. Check the provider before creating another annotation.",
            }),
          );
        }
        setError(
          failure instanceof Error
            ? failure.message
            : "Annotation creation failed",
        );
      } finally {
        saving.current.delete(draft.candidate.id);
      }
    }
    setContent(
      <Dialog
        open
        onClose={() => {
          if (saving.current.size === 0) setDraft(undefined);
        }}
      >
        <Stack sx={{ p: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {draft.screenshotWarning && (
            <Alert severity="warning">{draft.screenshotWarning}</Alert>
          )}
          <CreateAnnotationForm
            allowExternalMarkups
            initialAttachment={draft.initialAttachment}
            initialTitle={`Potential ${draft.candidate.method ? "surface crossing" : draft.candidate.kind}: ${analysis.cadReference.name}`}
            initialDescription={`CAD comparison: ${analysis.cadReference.name}\nComponent: ${draft.candidate.componentId}\nGrouped findings: ${draft.memberIds.length}\nCategory: ${draft.candidate.method ?? draft.candidate.kind}\n${clashDistanceLabel(draft.candidate)}: ${draft.candidate.distance} m\nTolerance: ${analysis.tolerance} m\nRequired clearance: ${analysis.clashClearance ?? 0} m\nEvidence: ${draft.candidate.support} occupied cells\nNominal source spacing: ${draft.candidate.samplingSpacing === undefined ? "Unavailable" : `${draft.candidate.samplingSpacing} m`}\nCoverage: ${analysis.clashes?.coverage}\n${draft.candidate.reason ?? "Potential clash requiring review"}`}
            onClose={() => {
              if (saving.current.size === 0) setDraft(undefined);
            }}
            onSave={save}
          />
        </Stack>
      </Dialog>,
    );
    return () => setContent(undefined);
  }, [
    draft,
    error,
    analysis,
    writable,
    camera,
    createAnnotation,
    dispatch,
    setContent,
    store,
  ]);
}

/** @returns Production numbered clash markers and the selected observed extent. */
export function CadClashMarkers({
  analysis,
  candidate,
  visible,
}: {
  analysis: CadPointCloudAnalysis;
  candidate?: ClashCandidate;
  visible: boolean;
}): JSX.Element {
  const parent = useViewOverlayRef();
  const dispatch = useAppDispatch();
  const origin = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashReviewOrigins?.[analysis.id],
  );
  const groups = selectClashReviewGroups(analysis, origin);
  const selectedGroup = groups.find((group) =>
    group.memberIds.includes(candidate?.id ?? ""),
  );
  const markers = groups
    .filter(
      ({ candidate: item }) =>
        item.status !== "dismissed" &&
        (item.status === "open" || item.id === selectedGroup?.candidate.id),
    )
    .slice(0, 100);
  if (
    candidate &&
    candidate.status !== "dismissed" &&
    selectedGroup &&
    !markers.includes(selectedGroup)
  ) {
    markers.push(selectedGroup);
  }
  return (
    <>
      {visible &&
        markers.map(({ candidate: item, number }, index) => (
          <TextLabel
            key={item.id}
            visible
            index={index}
            position={new Vector3().fromArray(item.position)}
            parentRef={parent}
            active
            transparent={false}
            pointerEvents="auto"
            placement={item.id === candidate?.id ? "top" : undefined}
            padding={0.25}
            backgroundColor={item.id === candidate?.id ? "#722727" : "#252525"}
          >
            <Tooltip
              title={`Potential ${item.method ? "surface crossing" : item.kind}; location marker (may be occluded)`}
            >
              <ButtonBase
                aria-label={`Focus clash ${number}`}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  dispatch(
                    selectClash({
                      analysisId: analysis.id,
                      candidateId: item.id,
                    }),
                  );
                }}
                sx={{ width: 48, height: 28, gap: 0.5, color: "white" }}
              >
                <WarningAmberIcon
                  fontSize="small"
                  sx={{
                    color: item.kind === "penetration" ? "#ff7373" : "#ffd166",
                  }}
                />
                {number}
              </ButtonBase>
            </Tooltip>
          </TextLabel>
        ))}
      {visible && candidate && candidate.status !== "dismissed" && (
        <>
          <ClashOutline
            candidate={candidate}
            boundsFrame={analysis.clashes?.boundsFrame}
          />
          <ClashEvidenceOverlay candidate={candidate} />
        </>
      )}
    </>
  );
}

function ClashEvidenceOverlay({
  candidate,
}: {
  candidate: ClashCandidate;
}): JSX.Element {
  const overlay = useMemo(() => {
    const group = new Group();
    group.position.fromArray(candidate.position);
    function geometryFor(
      points: Array<ClashCandidate["position"]>,
    ): BufferGeometry {
      return new BufferGeometry().setAttribute(
        "position",
        new BufferAttribute(
          new Float32Array(
            points.flatMap((point) =>
              new Vector3().fromArray(point).sub(group.position).toArray(),
            ),
          ),
          3,
        ),
      );
    }
    if (candidate.samples?.length) {
      const samples = new Points(
        geometryFor(candidate.samples),
        new PointsMaterial({
          color: "#ffd166",
          size: 6,
          sizeAttenuation: false,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      samples.renderOrder = 1;
      group.add(samples);
    }
    if (candidate.witness) {
      const line = new LineSegments(
        geometryFor([candidate.witness.scan, candidate.witness.cad]),
        new LineBasicMaterial({
          color: "#00e5ff",
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      line.renderOrder = 2;
      const endpoint = new Points(
        geometryFor([candidate.witness.cad]),
        new PointsMaterial({
          color: "#00e5ff",
          size: 8,
          sizeAttenuation: false,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      endpoint.renderOrder = 3;
      group.add(line, endpoint);
    }
    for (const object of group.children) object.raycast = () => undefined;
    return group;
  }, [candidate.samples, candidate.witness, candidate.position]);
  useEffect(
    () => () => {
      for (const object of overlay.children) {
        if (object instanceof Points || object instanceof LineSegments) {
          object.geometry.dispose();
          for (const material of Array.isArray(object.material)
            ? object.material
            : [object.material]) {
            material.dispose();
          }
        }
      }
    },
    [overlay],
  );
  return <primitive object={overlay} />;
}

function ClashOutline({
  candidate,
  boundsFrame,
}: {
  candidate: ClashCandidate;
  boundsFrame?: number[];
}): JSX.Element {
  const outline = useMemo(
    () =>
      new Box3Helper(
        clashCandidateBounds(candidate, boundsFrame).expandByScalar(0.01),
        OUTLINE_COLOR,
      ),
    [candidate, boundsFrame],
  );
  useEffect(
    () => () => {
      outline.geometry.dispose();
      for (const material of Array.isArray(outline.material)
        ? outline.material
        : [outline.material]) {
        material.dispose();
      }
    },
    [outline],
  );
  outline.raycast = () => undefined;
  return <primitive object={outline} />;
}
